import { ChatMessagePayload, ReportPayload } from '../types';
import { logTechnicalError } from './analytics';
import { storage, auth } from './firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { CANCEL_CHAT_API_URL, CHAT_API_URL, REPORT_API_URL } from '../config/runtime';

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
};

const isExpectedAbortError = (error: unknown, signal?: AbortSignal): boolean => {
  if (signal?.aborted) return true;
  if (!error || typeof error !== 'object') return false;

  const errorName = 'name' in error ? String((error as { name?: unknown }).name) : '';
  return errorName === 'AbortError';
};

const getNetworkErrorKind = (error: unknown): string | null => {
  if (!error || typeof error !== 'object') return null;

  const errorName = 'name' in error ? String((error as { name?: unknown }).name) : '';
  const errorMessage = 'message' in error ? String((error as { message?: unknown }).message) : '';
  const normalizedMessage = errorMessage.toLowerCase();

  if (errorName !== 'TypeError') return null;
  if (normalizedMessage.includes('load failed')) return 'load_failed';
  if (normalizedMessage.includes('failed to fetch')) return 'failed_to_fetch';
  if (normalizedMessage.includes('networkerror')) return 'network_error';
  if (normalizedMessage.includes('network request failed')) return 'network_request_failed';

  return null;
};

const getSendMessageTags = (payload: ChatMessagePayload) => ({
  feature: 'chat',
  operation: 'send_message',
  has_audio: Boolean(payload.audioUrl),
  has_image: Boolean(payload.imageUrl || payload.imageUrls?.length),
  has_text: Boolean(payload.message),
  has_client_message_id: Boolean(payload.clientMessageId)
});

const getStringValue = (value: unknown): string | undefined => (
  typeof value === 'string' && value.trim() ? value : undefined
);

const getObjectOutput = (value: unknown): Record<string, unknown> | undefined => {
  if (!isRecord(value)) return undefined;
  return isRecord(value.output) ? value.output : undefined;
};

export interface SendMessageResponse {
  text: string;
  title?: string;
  accepted: boolean;
  responsePersisted: boolean;
  usageIncremented: boolean;
  generationCanceled: boolean;
  botMessageId?: string;
  requestId?: string;
}

interface ApiErrorWithMeta extends Error {
  requestId?: string;
  errorKind?: string;
  status?: number;
}

const extractAcceptedResponse = (
  data: unknown,
  responseMeta: Omit<SendMessageResponse, 'text'>
): SendMessageResponse | null => {
  const item = Array.isArray(data) && data.length > 0 ? data[0] : data;
  const acceptedCandidate = getObjectOutput(item) ?? item;

  if (!responseMeta.accepted && (!isRecord(acceptedCandidate) || acceptedCandidate.accepted !== true)) {
    return null;
  }

  return {
    text: '',
    ...responseMeta,
    accepted: true,
    botMessageId: getStringValue(isRecord(acceptedCandidate) ? acceptedCandidate.botMessageId : undefined) || responseMeta.botMessageId
  };
};

const extractReplyResponse = (
  data: unknown,
  responseMeta: Omit<SendMessageResponse, 'text'>
): SendMessageResponse | null => {
  const item = Array.isArray(data) && data.length > 0 ? data[0] : data;

  if (isRecord(item) && typeof item.reply === 'string') {
    return {
      text: item.reply,
      title: getStringValue(item.chatTitle),
      ...responseMeta
    };
  }

  const output = getObjectOutput(item);
  if (output && typeof output.reply === 'string') {
    return {
      text: output.reply,
      title: getStringValue(output.chatTitle),
      ...responseMeta
    };
  }

  if (isRecord(item)) {
    const text = getStringValue(item.output) || getStringValue(item.text) || getStringValue(item.message);
    if (text) return { text, ...responseMeta };
  }

  if (typeof data === 'string') return { text: data, ...responseMeta };

  return null;
};

export const isRecoverableSendMessageError = (error: unknown, signal?: AbortSignal): boolean => {
  return !isExpectedAbortError(error, signal) && Boolean(getNetworkErrorKind(error));
};

export const getSendMessageNetworkErrorKind = (error: unknown): string | null => getNetworkErrorKind(error);

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };

  try {
    const token = await auth.currentUser?.getIdToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  } catch (error) {
    console.warn('No se pudo obtener el ID token para persistencia de respuesta.', error);
  }

  return headers;
};

const requestChatResponse = async (payload: ChatMessagePayload, signal?: AbortSignal): Promise<SendMessageResponse> => {
  const headers = await getAuthHeaders();
  const response = await fetch(CHAT_API_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
    signal, // Pasamos el signal para poder abortar
  });

  const requestId = response.headers.get('X-Juris-Request-Id') || undefined;
  const responseMeta = {
    accepted: response.headers.get('X-Juris-Response-Accepted') === 'true',
    responsePersisted: response.headers.get('X-Juris-Response-Persisted') === 'true',
    usageIncremented: response.headers.get('X-Juris-Usage-Incremented') === 'true',
    generationCanceled: response.headers.get('X-Juris-Generation-Canceled') === 'true',
    botMessageId: response.headers.get('X-Juris-Bot-Message-Id') || undefined,
    requestId
  };

  if (!response.ok) {
    const errorData = await response.json().catch((): unknown => ({}));
    const errorMsg = isRecord(errorData) ? getStringValue(errorData.error) : undefined;
    const apiError: ApiErrorWithMeta = new Error(`API ${response.status}: ${errorMsg || response.statusText || 'Unknown API Error'}`);
    const errorKind = response.headers.get('X-Juris-Error-Kind') || getStringValue(isRecord(errorData) ? errorData.errorKind : undefined);
    apiError.requestId = requestId || getStringValue(isRecord(errorData) ? errorData.requestId : undefined);
    apiError.errorKind = errorKind;

    // Reportamos el error con el status HTTP y GA4.
    // El objeto 'apiError' se marca internamente para evitar doble captura.
    logTechnicalError(apiError, `api:sendChatMessage:http:${response.status}`, {
      tags: {
        ...getSendMessageTags(payload),
        status_code: response.status,
        request_id: apiError.requestId,
        error_kind: errorKind
      },
      extra: {
        statusCode: response.status,
        requestId: apiError.requestId,
        errorKind,
        hasAudio: Boolean(payload.audioUrl),
        hasImage: Boolean(payload.imageUrl || payload.imageUrls?.length),
        hasText: Boolean(payload.message)
      }
    });

    throw apiError;
  }

  const data = await response.json().catch((): unknown => ({}));

  const acceptedResponse = extractAcceptedResponse(data, responseMeta);
  if (acceptedResponse) return acceptedResponse;

  const replyResponse = extractReplyResponse(data, responseMeta);
  if (replyResponse) return replyResponse;

  // Una respuesta aceptada sin el contrato esperado indica un error de integración.
  logTechnicalError(new Error("Respuesta del servidor con formato inesperado"), "api:sendChatMessage:invalid_format", {
    tags: getSendMessageTags(payload)
  });
  return { text: "Entendido (Sin respuesta de texto del servidor).", ...responseMeta };
};

export const uploadFileToFirebase = async (file: Blob | File, fileName: string = 'file', chatId?: string): Promise<string> => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("Usuario no autenticado para subir archivos.");

    // Las nuevas imágenes y grabaciones quedan agrupadas por chat para su eliminación.
    const uniqueName = `${Date.now()}_${crypto.randomUUID()}_${fileName}`;
    const storageRef = ref(storage, `chat_attachments/${user.uid}/${chatId ? `${chatId}/` : ''}${uniqueName}`);

    // Subir archivo
    const snapshot = await uploadBytes(storageRef, file);

    // Obtener URL pública (Tokenizada)
    const downloadURL = await getDownloadURL(snapshot.ref);

    return downloadURL;
  } catch (error) {
    console.error("Error subiendo archivo a Firebase Storage:", error);
    logTechnicalError(error, "api:uploadFileToFirebase");
    throw error;
  }
};

export const deleteUploadedChatImages = async (urls: string[]) => {
  await Promise.allSettled(urls.map((url) => deleteObject(ref(storage, url))));
};

export const uploadChatImages = async (images: File[], chatId: string): Promise<string[]> => {
  const results = await Promise.allSettled(images.map((image) => uploadFileToFirebase(image, image.name, chatId)));
  const uploaded = results.filter((result): result is PromiseFulfilledResult<string> => result.status === 'fulfilled');
  const failure = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
  if (failure) {
    await deleteUploadedChatImages(uploaded.map((result) => result.value));
    throw failure.reason;
  }
  return uploaded.map((result) => result.value);
};

export const sendChatMessage = async (payload: ChatMessagePayload, signal?: AbortSignal): Promise<SendMessageResponse> => {
  try {
    return await requestChatResponse(payload, signal);

  } catch (error) {
    if (isExpectedAbortError(error, signal)) {
      throw error;
    }

    const networkErrorKind = getNetworkErrorKind(error);
    if (networkErrorKind) {
      console.error('Error sending message:', error);
      throw error;
    }

    console.error('Error sending message:', error);
    logTechnicalError(error, "api:sendChatMessage:catch", {
      tags: {
        ...getSendMessageTags(payload),
        retry_count: 0
      }
    });
    throw error;
  }
};

export const cancelMessageGeneration = async (payload: Pick<ChatMessagePayload, 'userId' | 'chatId' | 'clientMessageId'> & { botMessageId?: string }) => {
  if (!CANCEL_CHAT_API_URL || !payload.clientMessageId) return { canceled: false, activeAbortSent: false };

  const headers = await getAuthHeaders();
  const response = await fetch(CANCEL_CHAT_API_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const errorData = await response.json().catch((): unknown => ({}));
    const errorKind = response.headers.get('X-Juris-Error-Kind') || getStringValue(isRecord(errorData) ? errorData.errorKind : undefined);
    const apiError: ApiErrorWithMeta = new Error(`Cancel API ${response.status}: ${response.statusText || 'Unknown API Error'}`);
    apiError.requestId = response.headers.get('X-Juris-Request-Id') || getStringValue(isRecord(errorData) ? errorData.requestId : undefined);
    apiError.errorKind = errorKind;
    logTechnicalError(apiError, `api:cancelMessageGeneration:http:${response.status}`, {
      tags: {
        feature: 'chat',
        operation: 'cancel_generation',
        status_code: response.status,
        request_id: apiError.requestId,
        error_kind: errorKind
      }
    });
    throw apiError;
  }

  const data = await response.json().catch((): unknown => ({}));
  return {
    canceled: isRecord(data) ? data.canceled === true : true,
    activeAbortSent: isRecord(data) ? data.activeAbortSent === true : false
  };
};

export const sendReport = async (payload: ReportPayload) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(REPORT_API_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorData = await response.json().catch((): unknown => ({}));
      const apiError = new Error('No se pudo enviar el reporte') as ApiErrorWithMeta;
      apiError.status = response.status;
      apiError.requestId = response.headers.get('X-Juris-Request-Id')
        || getStringValue(isRecord(errorData) ? errorData.requestId : undefined);
      apiError.errorKind = response.headers.get('X-Juris-Error-Kind')
        || getStringValue(isRecord(errorData) ? errorData.errorKind : undefined);
      throw apiError;
    }
    return response.json().catch((): unknown => ({ ok: true }));
  } catch (error) {
    const apiError = error as ApiErrorWithMeta;
    logTechnicalError(apiError, `api:report:${apiError.status || getNetworkErrorKind(error) || 'unknown'}`, {
      tags: {
        feature: 'report',
        operation: 'submit',
        status_code: apiError.status,
        request_id: apiError.requestId,
        error_kind: apiError.errorKind || getNetworkErrorKind(error)
      }
    });
    throw error;
  }
};
